import type { Block, Line, Cell } from '@/data';
import { BLC } from '@/data';
import type { Image3DRowFormat, Image3DSyncFormat } from '@/formats/UnitParser';

function generateOnsetX(cell: Cell) {
  return {
    onset: cell.offset,
    x: cell.offset * BLC.pixelByOnset,
  };
}

function addFirstOnsetX(onsetXList: { onset: number; x: number; }[], firstOnsetForTheRow: number) {
  onsetXList.unshift({
    onset: firstOnsetForTheRow,
    x: firstOnsetForTheRow * BLC.pixelByOnset,
  });
}

function alignOnsetXToTheLeft(onsetX: { onset: number; x: number; }, firstOnsetForTheRow: number) {
  return {
    onset: onsetX.onset,
    x: onsetX.x - firstOnsetForTheRow * BLC.pixelByOnset,
  };
}

function generateOnsetXList(line: Line) {
  const firstOnsetForTheRow = line.cells[0].onset;

  const onsetXList = line.cells.map(generateOnsetX);

  addFirstOnsetX(onsetXList, firstOnsetForTheRow);

  const alignedOnsetXList = onsetXList
    .map((onsetX) => alignOnsetXToTheLeft(onsetX, firstOnsetForTheRow));

  return alignedOnsetXList;
}

function generateRow(line: Line, rowIndex: number): Image3DRowFormat {
  const onsetXList = generateOnsetXList(line);

  const row: Image3DRowFormat = {
    'onset-x': onsetXList,
    height: BLC.rowHeight,
    y: rowIndex * BLC.rowHeight,
    'start-x': 0,
    'end-x': line.duration * BLC.pixelByOnset,
  };
  return row;
}

function generateRows(lines: Line[]) {
  return lines.map((line, rowIndex) => generateRow(line, rowIndex));
}

function generateRowsWithMargin(block: Block, blockVerticalMargin: number) {
  const rows: Image3DRowFormat[] = generateRows(block.lines).map((row) => ({
    ...row,
    y: row.y + blockVerticalMargin,
  }));

  return rows;
}

function sumPreviousBlocksHeight(blocksArray: Block[], blockIndex: number) {
  const sumBlocksHeight = blocksArray
    .filter((b, i) => i <= blockIndex)
    .map((b) => b.getHeight())
    .reduce((p, c) => p + c);
  const lastHeight = (blocksArray?.at(blockIndex)
    ?.getHeight() || 0);
  const blockVerticalMargin = sumBlocksHeight - lastHeight;
  return blockVerticalMargin;
}

export function generatePositions(blocks: Block[]): Image3DSyncFormat {
  const rows = blocks.flatMap((block, blockIndex, blocksArray) => {
    const blockVerticalMargin = sumPreviousBlocksHeight(blocksArray, blockIndex);
    return generateRowsWithMargin(block, blockVerticalMargin);
  });

  const page = { rows, image: 'undefined' };
  return { pages: [page] } as Image3DSyncFormat;
}
